import http from "@http";
import config from "@config";
import { sortToString } from "@transformers/sort";
import { transformFilters, OPERATORS } from "@transformers/request";
import actionCreators from "@modules/employee/actionCreator";
import employmentDetailsSaga from "./employment-details";
import personalDetailsSaga from "./personal-details";
import employeeSearchSaga from "./employee-search";
import medicalDetailsSaga from "./medical-details";
import userAccountSaga from "./user-account-details";
import {
  GetActiveClients,
  GetAllActiveClientsInformation,
  GetEmployee,
  ExportEmployeeReferralHistory,
  ExportEmployeeReferralHistorySuccess,
  GetEmployeeReferralHistory,
  GetEmployeeActionSummary,
  GetEmployeeChangelog,
  SearchEmployeeByName,
  GetEmployees,
  SetEmployeesSort,
  GetEmployeeSchema,
  GetAddEmployeeSchema,
  GetEmployeeTempSchema,
  ExportEmployeeDeclinedReferralHistory,
  ExportEmployeeDeclinedReferralHistorySuccess,
} from "@modules/employee/constants/actions";
import { all, call, put, takeEvery, takeLatest, select, spawn } from "redux-saga/effects";
import {
  getChangelogSort,
  getEmployeeFilters,
  getEmployeeNameSearchform,
  getEmployeesSort,
} from "@modules/employee/selectors";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

export function* onGetEmployeeReferralHistory({ payload }) {
  const { clientId, employeeId } = payload;

  if (!clientId || !employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/reports/client/${clientId}/employee/${employeeId}/referrals`,
    );

    yield put(actionCreators.GetEmployeeReferralHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeReferralHistoryError(e.response));
  }
}

export function* onExportEmployeeReferralHistory({ payload }) {
  const { clientId, employeeId } = payload;

  if (!clientId || !employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/reports/client/${clientId}/employee/${employeeId}/referrals/export`,
    );

    yield put(actionCreators.ExportEmployeeReferralHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportEmployeeReferralHistoryError(e.response));
  }
}

export function* onExportEmployeeReferralHistorySuccess({ payload }) {
  yield downloadCsvFile(payload, "WellbeingReferrals.csv");
}

export function* onGetEmployeeSchema({ payload }) {
  const clientId = payload.clientId;
  try {
    const schema = yield parseSchema("EmployeeDTO", { clientId });

    yield put(actionCreators.getEmployeeSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.getEmployeeSchemaError(e.response));
  }
}

export function* onGetEmployeeTempSchema({ payload }) {
  const clientId = payload.clientId;
  try {
    const schema = yield parseSchema("TemporaryEmployeeDTO", { clientId });
    yield put(actionCreators.getEmployeeSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.getEmployeeSchemaError(e.response));
  }
}

export function* onGetAddEmployeeSchema({ payload }) {
  try {
    const schema = yield parseSchema("EmployeeDTO", { clientId: payload.clientId });

    yield put(actionCreators.getAddEmployeeSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.getAddEmployeeSchemaError(e.response));
  }
}

export function* onGetEmployee({ payload }) {
  const clientId = payload.clientId;
  const employeeId = payload.employeeId;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}`,
    );
    yield put(actionCreators.getEmployeeSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getEmployeeError(e.response));
  }
}

export function* onGetEmployeeActionSummary({ payload }) {
  const clientId = payload.clientId;
  const employeeId = payload.employeeId;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}/summary`,
    );
    yield put(actionCreators.getEmployeeActionSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getEmployeeActionSummaryError(e.response));
  }
}

export function* onGetEmployeeChangelog({ payload }) {
  const clientId = payload.clientId;
  const employeeId = payload.employeeId;
  const sort = yield select(getChangelogSort);
  const page = payload.page || 1;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}/changelog`,
      {
        params: {
          page,
          sort: sortToString(sort),
          limit: config.resultsPerPage,
        },
      },
    );
    yield put(actionCreators.getEmployeeChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getEmployeeChangelogError(e.response));
  }
}

export function* onGetActiveClients({ payload }) {
  const limit = payload.limit || 100;
  const { name, is_operating_company } = payload;
  const status = payload.status || "ACTIVE";

  const search = transformFilters(Object.assign({}, { name, status, is_operating_company }), {
    name: OPERATORS.LIKE,
  });

  const params = {
    search,
    limit,
  };
  try {
    const response = yield call(http.get, `${config.endpointBaseUrl}api/v1/client-admin/client`, {
      params,
    });
    yield put(actionCreators.getActiveClientsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getActiveClientsError(e.response));
  }
}

export function* onSearchEmployeeByName({ payload }) {
  const filters = yield select(getEmployeeNameSearchform);
  const sort = yield select(getEmployeesSort);
  const additionalFilters = payload.filters || {};
  const page = payload.page || 1;
  const sortstr = sort ? `?sort=${sortToString(sort)}` : ""; // The only way to use double criteria sorting
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee${sortstr}`,
      {
        params: {
          search: transformFilters(Object.assign({}, filters, additionalFilters), {
            last_name: OPERATORS.LIKE,
            email: OPERATORS.LIKE,
          }),
          page,
          limit: config.resultsPerPage,
        },
      },
    );
    if (payload.filters && payload.filters.id) {
      const currentEmployee = response.data.content.find(
        employee => employee.id === payload.filters.id,
      );
      yield put(actionCreators.getCurrentEmployeeSuccess(currentEmployee));
    }

    yield put(actionCreators.getEmployeesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getEmployeesError(e.response));
  }
}

export function* onGetEmployees({ payload }) {
  const filters = payload.filters;
  const clientId = payload.clientId;
  const sort = payload.sort;
  const page = payload.page || 1;
  const additionalFilters = payload.filters ? { ...filters, ...payload.filters } : {};
  const sortstr = sort ? `?sort=${sortToString(sort)}` : ""; // The only way to use double criteria sorting

  delete filters.filters;
  const filters2 = {
    ...filters,
    ...additionalFilters,
  };

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee${sortstr}`,
      {
        params: {
          search: transformFilters(filters2, {
            id: OPERATORS.EQUALS,
            last_name: OPERATORS.LIKE,
            email: OPERATORS.LIKE,
            organisational_unit: OPERATORS.LIKE,
            home_mobile: OPERATORS.LIKE,
          }),
          page,
          limit: config.resultsPerPage,
        },
      },
    );
    if (payload.filters && payload.filters.id) {
      const currentEmployee = response.data.content.find(
        employee => employee.id === payload.filters.id,
      );
      yield put(actionCreators.GetCurrentEmployeeSuccess(currentEmployee));
    }

    yield put(actionCreators.GetEmployeesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeesError(e.response));
  }
}

export function* onExportEmployeeDeclinedReferralHistory({ payload }) {
  const { clientId, employeeId } = payload;

  if (!clientId || !employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${clientId}/employee/${employeeId}/referrals/export/declined`,
    );

    yield put(actionCreators.ExportEmployeeDeclinedReferralHistorySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportEmployeeDeclinedReferralHistoryError(e.response));
  }
}

export function* onExportEmployeeDeclinedReferralHistorySuccess({ payload }) {
  yield downloadCsvFile(payload, "WellbeingDeclinedReferrals.csv");
}

export function* EmployeeSaga() {
  yield all([
    // Personal Details
    takeEvery(GetEmployee, onGetEmployee),
    takeEvery(ExportEmployeeReferralHistory, onExportEmployeeReferralHistory),
    takeEvery(ExportEmployeeReferralHistorySuccess, onExportEmployeeReferralHistorySuccess),
    takeEvery(ExportEmployeeDeclinedReferralHistory, onExportEmployeeDeclinedReferralHistory),
    takeEvery(
      ExportEmployeeDeclinedReferralHistorySuccess,
      onExportEmployeeDeclinedReferralHistorySuccess,
    ),
    takeEvery(GetEmployeeReferralHistory, onGetEmployeeReferralHistory),
    takeEvery(GetEmployeeActionSummary, onGetEmployeeActionSummary),
    takeEvery(GetEmployeeChangelog, onGetEmployeeChangelog),
    takeLatest(GetEmployeeSchema, onGetEmployeeSchema),
    spawn(personalDetailsSaga),

    // Employment Details
    spawn(employmentDetailsSaga),

    // Medical Details
    spawn(medicalDetailsSaga),

    // User Account
    spawn(userAccountSaga),

    // Employee Search
    takeEvery(GetActiveClients, onGetActiveClients),
    takeEvery(SearchEmployeeByName, onSearchEmployeeByName),
    takeEvery(GetEmployees, onGetEmployees),
    takeEvery(SetEmployeesSort, onGetEmployees),
    takeEvery(GetAddEmployeeSchema, onGetAddEmployeeSchema),
    spawn(employeeSearchSaga),

    takeEvery(GetEmployeeTempSchema, onGetEmployeeTempSchema),
  ]);
}

export default EmployeeSaga;
